import {
  isShareTargetPickerAvailable,
  openShareTargetPicker,
  generateInviteURL,
  sendInviteURL,
} from 'src/lib/sharing';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { makePayload } from 'src/lib/analytics';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { now, user } from 'src/StateObserver';
import { AnalyticsProperties } from '@play-co/gcinstant';
import { inviteMessageWithoutCTA } from 'src/replicant/chatbot/flex/invite';
import { getCreativeText } from 'src/creatives/text';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import i18n from 'src/lib/i18n/i18n';
import { captureGenericError } from 'src/lib/sentry';
import { track } from 'src/lib/analytics/utils';
import { buddiesGetCommonProps } from 'src/replicant/buddies/buddies.getters';
/**
 * Share a buddies invite via LINE Share Target Picker (preferred) or native share (fallback).
 */
export async function buddiesShareInvite(): Promise<void> {
  if (isShareTargetPickerAvailable()) {
    return openBuddiesLineSharePicker();
  }

  const data = await generateBuddiesShareURL();

  if (!data) {
    captureGenericError(
      'BuddiesShareURLError',
      'Failed to generate invite url',
    );
    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
    return;
  }

  await sendInviteURL(data);

  track('BuddiesRequestSent', {
    ...buddiesGetCommonProps(user(), now()),
    inviteMethod: 'native',
  });
}

/** Open LINE Share Target Picker with a buddies invite flex message. */
export async function openBuddiesLineSharePicker(): Promise<void> {
  const creativeAsset = getRandomBuddiesCreative();
  creativeAsset.image = StateObserver.replicant.getChatbotAssetUrl(
    creativeAsset.image,
  );
  const creativeText = getCreativeText('invite');

  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      $creativeAssetID: creativeAsset.id,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
      channel: 'SHARE' as AnalyticsProperties.Channel,
      feature: Feature.BUDDIES._,
      subFeature: Feature.BUDDIES.LINE_SHARE,
      $subFeature: Feature.BUDDIES.LINE_SHARE,
      webReferrer: user().id,
      $deepLink: { type: 'buddiesShareInvite', inviteMethod: 'line' },
    });

  const title = process.env.LINE_APP_NAME || 'ぶるぶるどーぶつ';
  const flexMessage = inviteMessageWithoutCTA({
    title,
    asset: creativeAsset,
    creative: creativeText,
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  await openShareTargetPicker(flexMessage, analyticsEventProperties);

  track('BuddiesRequestSent', {
    ...buddiesGetCommonProps(user(), now()),
    inviteMethod: 'line',
  });
}

/**
 * Share a buddies invite via native share / clipboard (non-LINE path).
 */
export async function buddiesShareInviteOthers(): Promise<void> {
  const data = await generateBuddiesShareURL();

  if (!data) {
    captureGenericError(
      'BuddiesShareURLError',
      'Failed to generate invite url',
    );
    await openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
    return;
  }

  await sendInviteURL(data);

  track('BuddiesRequestSent', {
    ...buddiesGetCommonProps(user(), now()),
    inviteMethod: 'native',
  });
}

/** Generate a playco.click short URL for native share fallback. */
async function generateBuddiesShareURL() {
  const creativeAsset = getRandomBuddiesCreative();

  return generateInviteURL({
    creativeAsset,
    text: '',
    data: {
      feature: Feature.BUDDIES._,
      $subFeature: Feature.BUDDIES.NATIVE_SHARE,
      $deepLink: { type: 'buddiesShareInvite', inviteMethod: 'native' },
    },
  });
}

const BUDDIES_CREATIVE_IDS = [
  'buddies/invite_01',
  'buddies/invite_02',
  'buddies/invite_03',
  'buddies/invite_04',
];

/** Pick a random locale-appropriate buddies invite creative asset. */
function getRandomBuddiesCreative() {
  const locale = StateObserver.getLocale();
  const base =
    BUDDIES_CREATIVE_IDS[
      Math.floor(Math.random() * BUDDIES_CREATIVE_IDS.length)
    ];
  const id = `${base}_${locale}`;
  return {
    id,
    image: id,
    noCTA: false,
  };
}
